import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch } from 'react-redux';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';
import withNonCanceledSearch from '../WithNonCanceledSearch';

import Panel from '../../../../Panels';
import { trackAnalyticsEvent } from '../../../../../actions';
import {
  AMPLITUDE_NAME_MAP,
  ANALYTICS_EVENTS,
  ANALYTICS_PROPERTIES,
} from '../../../../../lib/analytics';

const comparisonKey = key =>
  i18n.getStr(`components.identityDocumentComparison.${key}`);

const displayText = value => {
  if (value == null || value === '') return '-';
  if (typeof value === 'object') return '-';
  return value;
};

const ComparisonResult = ({ matched }) => {
  if (typeof matched !== 'boolean') return '-';

  const isMatch = matched;

  return (
    <span>
      <M.Icon
        aria-hidden='true'
        className={isMatch ? 'status-clear mr-1' : 'text-warning mr-1'}
        icon={isMatch ? 'CheckmarkFilled' : 'WarningAltFilled'}
      />
      {i18n.getStr(isMatch ? 'badges.clear' : 'badges.review')}
    </span>
  );
};

ComparisonResult.propTypes = {
  matched: PropTypes.bool,
};

ComparisonResult.defaultProps = {
  matched: undefined,
};

const providerColumnHeader = provider => {
  if (typeof provider !== 'string' || provider.trim() === '') {
    return comparisonKey('providerValue');
  }
  return provider;
};

const comparisonFieldShape = PropTypes.shape({
  declared: PropTypes.string,
  provider: PropTypes.string,
  matched: PropTypes.bool,
});

const COMPARISON_FIELDS = ['name', 'dateOfBirth'];

const MISMATCH_COPY = {
  name: 'nameDidNotMatch',
  dateOfBirth: 'dateOfBirthDidNotMatch',
};

const IdentityDocumentComparison = ({ details }) => {
  const providerLabel = providerColumnHeader(details.provider);
  const mismatchedFields = COMPARISON_FIELDS.filter(
    field => details[field]?.matched === false,
  );

  return (
    <>
      <p className='mb-1'>
        <strong>{comparisonKey('identityMatch')}</strong>
      </p>
      <p
        className='text-very-muted mb-3'
        data-testid='identity-document-validation-provider'
      >
        {i18n.getStr('components.identityDocumentComparison.comparedAgainst', {
          provider: providerLabel,
        })}
      </p>
      <M.TableContainer>
        <M.Table data-testid='identity-document-validation-comparison'>
          <M.TableHead>
            <M.TableRow>
              <M.TableHeader aria-label={comparisonKey('field')} scope='col' />
              <M.TableHeader scope='col'>
                {comparisonKey('declared')}
              </M.TableHeader>
              <M.TableHeader scope='col'>{providerLabel}</M.TableHeader>
              <M.TableHeader scope='col'>
                {comparisonKey('matched')}
              </M.TableHeader>
            </M.TableRow>
          </M.TableHead>
          <M.TableBody>
            {COMPARISON_FIELDS.map(field => {
              const row = details[field];
              return (
                <M.TableRow key={field}>
                  <M.TableCell>{comparisonKey(field)}</M.TableCell>
                  <M.TableCell>{displayText(row?.declared)}</M.TableCell>
                  <M.TableCell>{displayText(row?.provider)}</M.TableCell>
                  <M.TableCell>
                    <ComparisonResult matched={row?.matched} />
                  </M.TableCell>
                </M.TableRow>
              );
            })}
          </M.TableBody>
        </M.Table>
      </M.TableContainer>
      {mismatchedFields.length > 0 && (
        <ul
          className='mt-3 mb-0'
          data-testid='identity-document-validation-mismatches'
        >
          {mismatchedFields.map(field => (
            <li key={field}>{comparisonKey(MISMATCH_COPY[field])}</li>
          ))}
        </ul>
      )}
    </>
  );
};

IdentityDocumentComparison.propTypes = {
  details: PropTypes.shape({
    name: comparisonFieldShape,
    dateOfBirth: comparisonFieldShape,
    provider: PropTypes.string,
  }).isRequired,
};

const hasTrinsicResultDetails = details =>
  details != null && (details.name != null || details.dateOfBirth != null);

const GovernmentDocumentAuthentication = ({ search }) => {
  const dispatch = useDispatch();
  const trinsicResultDetails = search?.trinsicResultDetails;
  const showIdentityMatch = hasTrinsicResultDetails(trinsicResultDetails);

  return (
    <Panel
      data-testid='international-identity-document-validation'
      title={i18n.getStr('components.internationalIdentityDocumentValidation')}
      status={search.status}
    >
      {showIdentityMatch ? (
        <IdentityDocumentComparison details={trinsicResultDetails} />
      ) : (
        search.pdfUrl && (
          <a
            href={search.pdfUrl}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              dispatch(
                trackAnalyticsEvent(ANALYTICS_EVENTS.SCREENING_DOWNLOADED, {
                  [ANALYTICS_PROPERTIES.SCREENING_NAME]:
                    AMPLITUDE_NAME_MAP.identity_document_validation,
                }),
              );
            }}
          >
            {i18n.getStr('components.viewReport')}
          </a>
        )
      )}
    </Panel>
  );
};

GovernmentDocumentAuthentication.propTypes = {
  search: PropTypes.shape({
    status: PropTypes.string,
    pdfUrl: PropTypes.string,
    trinsicResultDetails: PropTypes.shape({
      name: comparisonFieldShape,
      dateOfBirth: comparisonFieldShape,
      provider: PropTypes.string,
    }),
  }),
};

GovernmentDocumentAuthentication.defaultProps = {
  search: null,
};

export default i18n.renderTranslation()(
  withNonCanceledSearch(GovernmentDocumentAuthentication),
);
